import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { InputComponent } from '@woolworthsnz/form';
import {
	CitrusAdService,
	FuelTagComponent,
	PriceComponent,
	ProductImageComponent,
	PriceMetaComponent,
	QuantitySelectorComponent,
	ProductService,
	StampComponent,
	TagComponent,
} from '@woolworthsnz/product';
import {
	AlertComponent,
	ApiService,
	AppSettingsService,
	BreakPointService,
	DatalayerService,
	FeatureService,
	LocalStorageService,
	LoggingService,
	ModalOverlayService,
	ModalRoutingService,
	PopupService,
	SentenceCasePipe,
} from '@woolworthsnz/styleguide';
import { IngredientProduct, ProductResponse } from '@woolworthsnz/trader-api';
import { NgIf, NgTemplateOutlet, CurrencyPipe } from '@angular/common';
import { TealiumUtagService } from 'libs/analytics/src/lib/services/utag.service';

@Component({
	selector: 'recipe-ingredient-stamp, [recipe-ingredient-stamp]',
	templateUrl: './ingredient-stamp.component.html',
	styleUrls: ['./ingredient-stamp.component.scss'],
	providers: [ApiService, ProductService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		InputComponent,
		NgIf,
		TagComponent,
		FuelTagComponent,
		ProductImageComponent,
		PriceMetaComponent,
		NgTemplateOutlet,
		QuantitySelectorComponent,
		AlertComponent,
		PriceComponent,
		CurrencyPipe,
		SentenceCasePipe,
	],
})
export class IngredientStampComponent extends StampComponent implements OnChanges {
	@Input() ingredientType: 'main' | 'pantry';
	@Output() selectionChange = new EventEmitter<{ sku: string; selected: boolean }>();
	@Output() quantityChange = new EventEmitter<{ sku: string | undefined; quantity: number }>();

	public linePrice: number;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	public _ingredient: IngredientProduct;

	formGroup: FormGroup;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	_selected: boolean;

	constructor(
		protected modalOverlayService: ModalOverlayService,
		public productService: ProductService,
		protected cdr: ChangeDetectorRef,
		protected appSettingsService: AppSettingsService,
		protected datalayerService: DatalayerService,
		protected citrusAdService: CitrusAdService,
		protected modalRoutingService: ModalRoutingService,
		protected featureService: FeatureService,
		protected router: Router,
		protected popupService: PopupService,
		protected localStorageService: LocalStorageService,
		protected breakPointService: BreakPointService,
		protected loggingService: LoggingService,
		protected tealiumUtagService: TealiumUtagService
	) {
		super(
			modalOverlayService,
			productService,
			cdr,
			appSettingsService,
			datalayerService,
			citrusAdService,
			modalRoutingService,
			featureService,
			router,
			popupService,
			localStorageService,
			breakPointService,
			loggingService,
			0,
			tealiumUtagService
		);
	}

	@Input()
	get selected(): boolean {
		return this._selected;
	}

	@Input()
	get ingredient(): IngredientProduct {
		return this._ingredient;
	}

	get quantityColSpan(): string {
		return this.productIsAvailable ? '1' : '2';
	}

	set selected(val: boolean) {
		this._selected = val && this.productIsAvailable;
	}

	set ingredient(val: IngredientProduct) {
		this.product = val.item || <ProductResponse>{};
		this._ingredient = val;
	}

	ngOnInit(): void {
		if (this.sku === undefined) {
			return;
		}
		this.formGroup = new FormGroup({
			[this.ingredientType + this.sku]: new FormControl({
				value: this._selected,
				disabled: !this.productIsAvailable,
			}),
		});
		this.linePrice = (this.product.quantity?.value || 0) * (this.product.price?.salePrice || 0);
	}

	ngOnChanges(): void {
		if (this.sku === undefined) {
			return;
		}
		this.formGroup
			?.get(this.ingredientType + this.sku)
			?.patchValue(this._selected, { emitEvent: false, onlySelf: true });
	}

	handleSelection(): void {
		if (this.sku === undefined) {
			return;
		}
		this.selectionChange.emit({
			sku: this.sku,
			selected: this.formGroup.value[this.ingredientType + this.sku],
		});
	}

	updateQuantityClick(event: any): void {
		this.linePrice = event * (this.product.price?.salePrice || 0);
		this.quantityChange.emit({ sku: this.product?.sku, quantity: event });
	}
}
